/**
 * Partner Portal API Service
 *
 * Wraps the backend partner and load-library endpoints.
 * Uses API_BASE_URL from flux-web's api.js (passed via context or import).
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

async function getAuthHeaders(currentUser) {
  const headers = { 'Content-Type': 'application/json' };
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (e) {
      console.error('Failed to get ID token:', e);
    }
  }
  return headers;
}

// ── Load Curve Library ──────────────────────────────────────────────────────

export async function saveCurve(currentUser, { label, loads_kw, source_file }) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-library/curves`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ label, loads_kw, source_file }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save curve');
  return data;
}

export async function listCurves(currentUser) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-library/curves`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to list curves');
  return data.curves;
}

export async function getCurve(currentUser, curveId) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-library/curves/${curveId}`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to get curve');
  return data;
}

export async function deleteCurve(currentUser, curveId) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-library/curves/${curveId}`, {
    method: 'DELETE',
    headers,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to delete curve');
  return data;
}

// ── Auto-Propose ────────────────────────────────────────────────────────────

export async function autoPropose(currentUser, { address, lat, lon, loads_kw, tariff_data, customer_name, curve_id }) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/partner/auto-propose`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ address, lat, lon, loads_kw, tariff_data, customer_name, curve_id }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to create proposal');
  return data;
}

// ── Tariff Lookup ───────────────────────────────────────────────────────────

export async function tariffsForLocation(currentUser, lat, lon) {
  const headers = await getAuthHeaders(currentUser);
  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/partner/tariffs-for-location?lat=${lat}&lon=${lon}`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch tariffs');
  return data.tariffs;
}

// ── Load Profile Upload ─────────────────────────────────────────────────────

export async function uploadLoadProfile(currentUser, file) {
  const token = currentUser ? await currentUser.getIdToken() : null;
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/load-profile/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'X-Frontend-Source': 'partner-portal',
    },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to upload load profile');
  return data;
}
